/* One sample per fresh document. No Admin token, writes, visitor IDs or persisted nonce. */
(function () {
  if (window.AdenzoCollectionRotation) return;
  const nonce = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Array.from(crypto.getRandomValues(new Uint8Array(24)), n => n.toString(16).padStart(2, '0')).join('');
  const samples = new Map();
  const processed = new WeakSet();
  function scopeIds(root, prefix) {
    const ids = new Map();
    // Preserve native QuickAddInfo-/ModalClose- prefixes used by custom-element constructors.
    root.querySelectorAll('[id]').forEach(node => { const before = node.id; node.id = before + '-' + prefix; ids.set(before, node.id); });
    root.querySelectorAll('[for], [form], [aria-labelledby], [aria-describedby], [aria-controls], [data-modal], [href]').forEach(node => {
      ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach(attr => { if (node.hasAttribute(attr)) node.setAttribute(attr, node.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' ')); });
      ['data-modal', 'href'].forEach(attr => { const value = node.getAttribute(attr); if (value?.startsWith('#') && ids.has(value.slice(1))) node.setAttribute(attr, '#' + ids.get(value.slice(1))); });
    });
  }
  // A row with nothing real to show is removed, never filled with placeholders.
  const hasRealCards = grid => Boolean(grid.querySelector(':scope > :not([data-adenzo-skeleton])'));
  function hideSection(grid, reason) {
    const section = grid.closest('.shopify-section');
    if (section) section.hidden = true;
    grid.dataset.rotationStatus = 'hidden-' + reason;
  }
  window.AdenzoCollectionVisibility = { hasRealCards, hideSection };
  async function rotate(grid) {
    if (processed.has(grid) || window.Shopify?.designMode || grid.dataset.rotationError === 'loading-timeout') return;
    processed.add(grid);
    try {
      const path = grid.dataset.proxy || '/apps/adenzo-collections';
      if (!/^\/apps\/[a-zA-Z0-9/_-]+$/.test(path)) throw new Error('invalid-proxy-path');
      const url = new URL(path, location.origin);
      url.searchParams.set('collection', grid.dataset.collectionId);
      url.searchParams.set('nonce', nonce);
      url.searchParams.set('ratio', grid.dataset.ratio || 'adapt');
      ['vendor', 'rating', 'secondary'].forEach(key => url.searchParams.set(key, grid.dataset[key] === 'true' ? '1' : '0'));
      const key = url.toString();
      if (!samples.has(key)) samples.set(key, fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000) }).then(response => { if (!response.ok) throw new Error('http-' + response.status); return response.text(); }));
      const html = await samples.get(key);
      if (!grid.isConnected || grid.dataset.rotationError === 'loading-timeout') return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-adenzo-selection]');
      if (!source) throw new Error('invalid-selection-response');
      // Scripts are not needed: custom elements connect on insertion. Never execute response scripts.
      source.querySelectorAll('script, iframe, object, embed').forEach(node => node.remove());
      source.querySelectorAll('*').forEach(node => Array.from(node.attributes).filter(a => /^on/i.test(a.name)).forEach(a => node.removeAttribute(a.name)));
      const template = grid.querySelector(':scope > div');
      // A looped carousel starts with a duplicate slide. Copy layout classes,
      // not Swiper's clone/active state: loopCreate removes duplicate-marked
      // nodes, which would otherwise delete every newly inserted product.
      const className = (template?.className || (grid.classList.contains('swiper-wrapper') ? 'swiper-slide' : 'grid__item'))
        .split(/\s+/).filter(name => name && !name.startsWith('swiper-slide-') && name !== 'adenzo-skeleton-card').join(' ');
      const cards = Array.from(source.querySelectorAll(':scope > [data-adenzo-card]'));
      if (cards.length > 20) throw new Error('selection-too-large');
      const fragment = document.createDocumentFragment();
      cards.forEach((card, i) => { card.className = className; card.id = grid.id + '-rotated-' + i; scopeIds(card, grid.id); fragment.append(card); });
      const swiper = grid.closest('[data-swiper-slider]')?.swiper;
      if (swiper?.params.loop) swiper.loopDestroy();
      grid.replaceChildren(fragment);
      if (swiper) { if (swiper.params.loop && cards.length) swiper.loopCreate(); swiper.update(); swiper.slideTo(0); }
      grid.dataset.selectionId = source.dataset.adenzoSelection;
      grid.dataset.rotationStatus = 'applied';
      delete grid.dataset.rotationError;
      if (!cards.length) hideSection(grid, 'empty');
      const shortfall = document.querySelector('[data-adenzo-shortfall="' + CSS.escape(grid.id) + '"]');
      if (shortfall) { shortfall.hidden = cards.length === 0 || cards.length >= Number(shortfall.dataset.requested); shortfall.textContent = shortfall.dataset.template.replace('[selected]', String(cards.length)); }
      document.dispatchEvent(new CustomEvent('adenzo:collection:rotated', { detail: { collectionId: grid.dataset.collectionId, selectionId: source.dataset.adenzoSelection, count: cards.length } }));
    } catch (error) {
      if (grid.dataset.rotationError === 'loading-timeout') return;
      grid.dataset.rotationStatus = 'fallback';
      // Controlled codes only: never expose response bodies, signed URLs or customer data.
      const reason = /^(http-\d{3}|invalid-proxy-path|invalid-selection-response|selection-too-large)$/.test(error?.message) ? error.message : 'network-or-render-error';
      grid.dataset.rotationError = reason;
      // Keep the server-rendered products if there are any; skeletons alone are not shown.
      if (!hasRealCards(grid)) hideSection(grid, 'failed');
      document.dispatchEvent(new CustomEvent('adenzo:collection:rotation-failed', { detail: { collectionId: grid.dataset.collectionId, reason } }));
    } finally {
      clearTimeout(grid.adenzoLoadingTimer);
      delete grid.dataset.adenzoLoading;
      grid.removeAttribute('aria-busy');
      grid.inert = false;
      const label = document.getElementById(grid.id + '-loading');
      if (label) label.hidden = true;
    }
  }
  function scan(root = document) { root.querySelectorAll('[data-adenzo-rotation]').forEach(rotate); }
  window.AdenzoCollectionRotation = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true }); else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
